import { experimentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { INCLUDE_SOURCE_SHIPPING_PARAM } from '~/utils/source-shipping';

import type {
  JobConfigChangeEntry,
  JobRunConfig,
  JobRunConfigUpdateResponse,
  UpdateJobRunConfigRequest,
} from '../types/job-run-config.types';
import type {
  CreateScheduledJobRequest,
  MetaProductScoreDetails,
  MetaScoreStage,
  PipelineEnrichedProductsResponse,
  SampleEnrichmentPreviewResponse,
  ScheduledContentScoringQualityScoreResponse,
  ScheduledJobResponse,
  ScheduledJobStatusResponse,
  ScheduledPipelineApprovalPayload,
  ScheduledPipelineApprovalResponse,
  ScheduledPipelineQualityScoreResponse,
  ScheduledSampleEnrichmentQualityScoreResponse,
  ScheduledTriggerStatusResponse,
  StartEnrichmentResponse,
  StartScheduledContentScoringRequest,
  StartScheduledContentScoringResponse,
  StartScheduledPipelineRequest,
  StartScheduledSampleEnrichmentRequest,
} from '../types/scheduled-enrichment.types';
import { fetchAllEnrichedProductPages } from '../utils/fetch-all-enriched-product-pages';

/**
 * Create (or retrieve an existing) scheduled-enrichment job session. The
 * The returned `jobId` anchors all three downstream trigger calls and is
 * used as the URL batch identifier and exit navigation target.
 */
export async function createScheduledJob(
  payload: CreateScheduledJobRequest,
): Promise<ScheduledJobResponse> {
  return experimentsClient
    .post(endpoints.scheduledEnrichment.jobs(), { json: payload })
    .json<ScheduledJobResponse>();
}

/**
 * Kick off the content-scoring workflow for a scheduled (non-experiment)
 * enrichment session. Returns a workflowId that must be polled via
 * `fetchScheduledTriggerStatus` until terminal — `null` when the operator
 * turned pre-scoring off, in which case there is nothing to poll.
 */
export async function triggerScheduledContentScoring(
  payload: StartScheduledContentScoringRequest,
): Promise<StartScheduledContentScoringResponse> {
  return experimentsClient
    .post(endpoints.scheduledEnrichment.contentScoringTrigger(), { json: payload })
    .json<StartScheduledContentScoringResponse>();
}

/**
 * Kick off the sample-enrichment workflow. Must be called after content-scoring
 * COMPLETED. Requires `contentScoringWorkflowId` so the backend can scope the
 * sample to the scored product pool.
 */
export async function triggerScheduledSampleEnrichment(
  payload: StartScheduledSampleEnrichmentRequest,
): Promise<StartEnrichmentResponse> {
  return experimentsClient
    .post(endpoints.scheduledEnrichment.sampleEnrichmentTrigger(), { json: payload })
    .json<StartEnrichmentResponse>();
}

/**
 * Kick off the full-catalog pipeline. Called from the Review & Refine step
 * CTA (non-split path) once the operator confirms sample quality. Requires
 * `contentScoringWorkflowId` to scope the pipeline to the scored product pool.
 */
export async function triggerScheduledPipeline(
  payload: StartScheduledPipelineRequest,
): Promise<StartEnrichmentResponse> {
  return experimentsClient
    .post(endpoints.scheduledEnrichment.pipelineTrigger(), { json: payload })
    .json<StartEnrichmentResponse>();
}

/**
 * Poll the current status of any scheduled trigger workflow (content-scoring,
 * sample-enrichment, or pipeline). Polled by `useScheduledTriggerStatus`
 * until status reaches a terminal value (COMPLETED | FAILED).
 */
export async function fetchScheduledTriggerStatus(
  workflowId: string,
): Promise<ScheduledTriggerStatusResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.triggerStatus(workflowId))
    .json<ScheduledTriggerStatusResponse>();
}

/**
 * Fetch aggregate quality scores once content-scoring COMPLETED. Drives the
 * Current Content Score page gauge and bar chart in the Meta Ads enrichment
 * funnel (same visual as the experiment flow, different endpoint).
 */
export async function fetchScheduledContentScoringQualityScore(
  workflowId: string,
): Promise<ScheduledContentScoringQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.contentScoringQualityScore(workflowId))
    .json<ScheduledContentScoringQualityScoreResponse>();
}

/**
 * Record the operator's APPROVED/REJECTED decision while the Meta pipeline is
 * paused at AWAITING_APPROVAL. One-shot — the server returns 409 on a second call.
 */
export async function submitScheduledPipelineApproval(
  workflowId: string,
  payload: ScheduledPipelineApprovalPayload,
): Promise<ScheduledPipelineApprovalResponse> {
  return experimentsClient
    .post(endpoints.scheduledEnrichment.pipelineApproval(workflowId), { json: payload })
    .json<ScheduledPipelineApprovalResponse>();
}

/**
 * Fetch Before/After/Lift quality scores from a completed scheduled sample-enrichment run.
 * Drives the Quality Score tab and agent panel score jump on the Review & Refine page.
 */
export async function fetchScheduledSampleEnrichmentQualityScore(
  workflowId: string,
): Promise<ScheduledSampleEnrichmentQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.sampleEnrichmentQualityScore(workflowId))
    .json<ScheduledSampleEnrichmentQualityScoreResponse>();
}

/**
 * Fetch Before/After/Lift quality scores from a completed Meta pipeline run.
 * Drives the score comparison card in the Enrichment Overview agent panel.
 */
export async function fetchScheduledPipelineQualityScore(
  workflowId: string,
): Promise<ScheduledPipelineQualityScoreResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.pipelineQualityScore(workflowId))
    .json<ScheduledPipelineQualityScoreResponse>();
}

/**
 * Fetch the full job-status snapshot including all three sub-workflow blocks
 * (contentScoring, sampleEnrichment, pipeline). Used by the catalog-operations
 * batch list to resume an in-progress Meta Ads batch at the correct funnel step.
 */
export async function fetchScheduledJobStatus(jobId: string): Promise<ScheduledJobStatusResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.jobStatus(jobId))
    .json<ScheduledJobStatusResponse>();
}

/**
 * Fetch the paginated Before/After product cards from a completed sample-enrichment run.
 * Drives the Preview Products tab on the Review & Refine page. `page` is zero-based.
 */
export async function fetchScheduledSampleEnrichmentPreview(
  workflowId: string,
  page = 0,
  size = 50,
): Promise<SampleEnrichmentPreviewResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.sampleEnrichmentPreview(workflowId), {
      searchParams: { page, size, ...INCLUDE_SOURCE_SHIPPING_PARAM },
    })
    .json<SampleEnrichmentPreviewResponse>();
}

/**
 * Fetch the paginated list of enriched products from a completed pipeline run.
 * Drives the enriched-products preview on the catalog-operations batch detail
 * page (post-exit). `page` is zero-based.
 */
export async function fetchScheduledPipelineEnrichedProducts(
  workflowId: string,
  page = 0,
  size = 20,
): Promise<PipelineEnrichedProductsResponse> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.pipelineEnrichedProducts(workflowId), {
      searchParams: { page, size, ...INCLUDE_SOURCE_SHIPPING_PARAM },
    })
    .json<PipelineEnrichedProductsResponse>();
}

/**
 * Every enriched product of a completed pipeline run, across all server pages
 * (`size` is capped at 200). The read spans both enrichment scopes — sample ∪
 * pipeline, deduplicated server-side on `externalId` — so a run whose full pass
 * had nothing new to curate still returns its sample-pass rows. Backs the
 * listings that filter and paginate client-side.
 */
export async function fetchAllScheduledPipelineEnrichedProducts(
  workflowId: string,
): Promise<PipelineEnrichedProductsResponse> {
  return fetchAllEnrichedProductPages((page, size) =>
    fetchScheduledPipelineEnrichedProducts(workflowId, page, size),
  );
}

/**
 * Fetch per-product Meta scoring detail for the drill-down modal.
 * Returns 404 when no scoring row exists for the given productId + stage.
 * Default stage is FULL_POST per the API contract.
 */
export async function fetchMetaProductScoreDetails(
  workflowId: string,
  productId: string,
  stage: MetaScoreStage = 'FULL_POST',
): Promise<MetaProductScoreDetails> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.metaProductScoreDetails(workflowId, productId), {
      searchParams: { stage },
    })
    .json<MetaProductScoreDetails>();
}

/**
 * Read a scheduled job's stored run config — the authoritative source the next trigger
 * inherits, so the funnel hydrates its local run config from this rather than a cached store.
 */
export async function getJobRunConfig(jobId: string): Promise<JobRunConfig> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.jobRunConfig(jobId))
    .json<JobRunConfig>();
}

/**
 * Edit a scheduled job's run config between runs. The server records the change (who/when/
 * reason) and returns the config after the edit; 409 while any phase of the job is running.
 */
export async function updateJobRunConfig(
  jobId: string,
  payload: UpdateJobRunConfigRequest,
): Promise<JobRunConfigUpdateResponse> {
  return experimentsClient
    .patch(endpoints.scheduledEnrichment.jobRunConfig(jobId), { json: payload })
    .json<JobRunConfigUpdateResponse>();
}

/** Fetch the job's run-config change history, newest first (edits and trigger restatements). */
export async function getJobRunConfigHistory(jobId: string): Promise<JobConfigChangeEntry[]> {
  return experimentsClient
    .get(endpoints.scheduledEnrichment.jobRunConfigHistory(jobId))
    .json<JobConfigChangeEntry[]>();
}
